import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Nav, Navbar, NavbarBrand, NavbarToggler, Collapse, Container, NavItem, NavLink } from 'reactstrap';
import MainMenuItem from './mainMenuItem';
import FixedHeader from './fixedHeader';
import HierarchicalHeader from './hierarchicalHeader';
import entityComposition from '../common/classes/entityComposition';
import appHistory from '../../utils/appHistory';
import Icon from '../common/containers/icon';
import FavoritesModal from '../favorites/favorites';
import { getAllFavorites, filterFavorites } from '../favorites/favoritesHelpers';
import { useUserPreferencesFavorites } from '../favorites/userPreferencesFavorites';
const handleLogout = () => appHistory.push('/logout');

// Custom hook for loading settings and config behaviors
const useHeaderInitialization = (cabinets, configBehaviorActions, settingsActions) => {
  useEffect(() => {
    // only need to load CustomerSettings and ConfigBehaviors once per
    // browser session. Reloading the page will reload the settings.
    if (cabinets) {
      return;
    }
    settingsActions.serverLoad();
    configBehaviorActions.serverIndex({});
  }, [cabinets, configBehaviorActions, settingsActions]);
};
const Header = _ref => {
  let configBehaviorActions = _ref.configBehaviorActions,
    _ref$dashboards = _ref.dashboards,
    dashboards = _ref$dashboards === void 0 ? [] : _ref$dashboards,
    _ref$cabinets = _ref.cabinets,
    cabinets = _ref$cabinets === void 0 ? null : _ref$cabinets,
    settingsActions = _ref.settingsActions,
    settingsLoaded = _ref.settingsLoaded,
    user = _ref.user,
    _ref$userContent = _ref.userContent,
    userContent = _ref$userContent === void 0 ? null : _ref$userContent,
    _ref$userActions = _ref.userActions,
    userActions = _ref$userActions === void 0 ? {} : _ref$userActions,
    _ref$tabGroups = _ref.tabGroups,
    tabGroups = _ref$tabGroups === void 0 ? [] : _ref$tabGroups,
    _ref$favoritesEnabled = _ref.favoritesEnabled,
    favoritesEnabled = _ref$favoritesEnabled === void 0 ? false : _ref$favoritesEnabled;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    favoritesModalOpen = _useState4[0],
    setFavoritesModalOpen = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    favoritesQuery = _useState6[0],
    setFavoritesQuery = _useState6[1];
  const _useUserPreferencesFa = useUserPreferencesFavorites(),
    preferencesContent = _useUserPreferencesFa.preferencesContent,
    ensurePreferencesFavoritesLoaded = _useUserPreferencesFa.ensureLoaded;
  const userId = user === null || user === void 0 ? void 0 : user.user_id;
  const loadUserSavedViews = userActions.serverShow;
  useEffect(() => {
    if (!favoritesEnabled || !userId || !settingsLoaded || !favoritesModalOpen) {
      return;
    }
    // Re-fetch from server so metaDeleted-form pins pruned since the last cached load are gone.
    ensurePreferencesFavoritesLoaded({
      force: true
    }).catch(() => {});
    if (loadUserSavedViews && userContent == null) {
      loadUserSavedViews(userId).catch(() => {});
    }
  }, [favoritesEnabled, userId, settingsLoaded, favoritesModalOpen, ensurePreferencesFavoritesLoaded, loadUserSavedViews, userContent]);
  const filteredFavorites = useMemo(() => {
    if (!user) return [];
    const cabinetDefinitions = cabinets || [];
    const allFavorites = getAllFavorites(userContent, cabinetDefinitions, preferencesContent);
    return filterFavorites(allFavorites, favoritesQuery);
  }, [user, userContent, cabinets, favoritesQuery, preferencesContent]);
  const handleOpenFavorites = () => {
    setFavoritesModalOpen(true);
    setFavoritesQuery('');
  };
  const handleCloseFavorites = () => {
    setFavoritesModalOpen(false);
    setFavoritesQuery('');
  };
  useHeaderInitialization(cabinets, configBehaviorActions, settingsActions);
  const hasGroupedCabinets = cabinets && cabinets.some(cabinet => {
    var _cabinet$client;
    return (cabinet === null || cabinet === void 0 || (_cabinet$client = cabinet.client) === null || _cabinet$client === void 0 || (_cabinet$client = _cabinet$client.web) === null || _cabinet$client === void 0 || (_cabinet$client = _cabinet$client.tabGroup) === null || _cabinet$client === void 0 ? void 0 : _cabinet$client.identifier) && cabinet.client.web.tabGroup.identifier !== 'admin';
  });
  const refersLegacyCabinets = cabinets && cabinets.some(cabinet => cabinet.identifier.includes('legacy'));
  const hasDirectFormCreation = cabinets && cabinets.some(cabinet => !!(cabinet !== null && cabinet !== void 0 && cabinet.directFormType));
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Navbar, {
    light: true,
    className: "navbar-default",
    expand: "md"
  }, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(NavbarBrand, {
    href: "/"
  }, /*#__PURE__*/React.createElement("img", {
    width: "80px",
    src: "/img/logos/truLogo.png",
    alt: "TRU Logo"
  })), /*#__PURE__*/React.createElement(NavbarToggler, {
    onClick: () => setIsOpen(!isOpen),
    className: "mr-2"
  }), /*#__PURE__*/React.createElement(Collapse, {
    isOpen: isOpen,
    navbar: true
  }, /*#__PURE__*/React.createElement(Nav, {
    tabs: true,
    navbar: true,
    className: "ml-auto navbar-right"
  }, hasGroupedCabinets || refersLegacyCabinets || hasDirectFormCreation ? /*#__PURE__*/React.createElement(HierarchicalHeader, {
    user: user,
    cabinets: cabinets,
    dashboards: dashboards,
    tabGroups: tabGroups,
    settingsLoaded: settingsLoaded
  }) : /*#__PURE__*/React.createElement(FixedHeader, {
    user: user,
    cabinets: cabinets,
    dashboards: dashboards,
    settingsLoaded: settingsLoaded
  }), favoritesEnabled && /*#__PURE__*/React.createElement(NavItem, {
    className: "t-h-nav-favorites",
    style: {
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(NavLink, {
    href: "#",
    onClick: event => {
      event.preventDefault();
      handleOpenFavorites();
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "star"
  }), /*#__PURE__*/React.createElement("br", null), "Favorites")), /*#__PURE__*/React.createElement(MainMenuItem, {
    className: "t-h-nav-logout",
    key: "logoutTab",
    onClick: handleLogout,
    glyph: "sign-out-alt",
    title: "Log Out"
  }))))), favoritesEnabled && /*#__PURE__*/React.createElement(FavoritesModal, {
    isOpen: favoritesModalOpen,
    onClose: handleCloseFavorites,
    query: favoritesQuery,
    onQueryChange: setFavoritesQuery,
    favorites: filteredFavorites
  }));
};
Header.propTypes = {
  cabinets: PropTypes.arrayOf(PropTypes.shape({
    glyph: PropTypes.string,
    directFormType: PropTypes.string
  })),
  configBehaviorActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  settingsActions: PropTypes.shape({
    serverLoad: PropTypes.func
  }).isRequired,
  settingsLoaded: PropTypes.bool.isRequired,
  dashboards: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    embedID: PropTypes.string
  })),
  tabGroups: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    icon: PropTypes.string
  })),
  user: PropTypes.shape({
    permissions: PropTypes.shape({
      formTypes: PropTypes.shape({}),
      general: PropTypes.arrayOf(PropTypes.string)
    }),
    user_id: PropTypes.string
  }).isRequired,
  favoritesEnabled: PropTypes.bool,
  userContent: PropTypes.shape({
    favorites: PropTypes.arrayOf(PropTypes.shape({})),
    savedViews: PropTypes.shape({})
  }),
  userActions: PropTypes.shape({
    change: PropTypes.func,
    serverSave: PropTypes.func,
    serverShow: PropTypes.func
  })
};
export { Header as TestHeader, useHeaderInitialization };
export default entityComposition(Header, ['settings', 'user'], {
  user: state => state.login.user,
  cabinets: state => state.settings.settings.cabinetDefinitions,
  tabGroups: state => state.settings.settings.tabGroupDefinitions,
  dashboards: state => {
    var _state$login;
    return (state === null || state === void 0 || (_state$login = state.login) === null || _state$login === void 0 || (_state$login = _state$login.user) === null || _state$login === void 0 || (_state$login = _state$login.superset) === null || _state$login === void 0 ? void 0 : _state$login.dashboards) || [];
  },
  settingsLoaded: state => state.settings.settings && Object.keys(state.settings.settings).length > 0,
  favoritesEnabled: state => {
    var _state$settings;
    return ((_state$settings = state.settings) === null || _state$settings === void 0 || (_state$settings = _state$settings.settings) === null || _state$settings === void 0 ? void 0 : _state$settings.favoritesEnabled) || false;
  },
  userContent: state => {
    var _state$user;
    return (_state$user = state.user) === null || _state$user === void 0 || (_state$user = _state$user.active) === null || _state$user === void 0 ? void 0 : _state$user.content;
  }
}, ['configBehavior']);